import { useDispatch, useSelector } from 'react-redux';
import { WorksheetEquipmentDropdown } from '../../Core';
import { Field, NumericInput, TextOutput, Checkbox, Dropdown } from '../../../Core';
import { ListFieldSection } from '../../../Layout';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { WorksheetRootComponent } from 'Components/Worksheets/Core/types';
import ids from '../../QestIds';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import pipe from 'Lib/Hooks/Selectors/pipe';
import { valueOrDefault } from 'Lib/Any';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { WorksheetBalanceFieldSet } from '../../Core/WorksheetEquipment';

const qestIds = [ids.oversizeCorrection.ARIZ_227e];

const Worksheet: WorksheetRootComponent = ({ createWorksheetSelector, prefixName }) => {
  const dispatch = useDispatch();
  const availableSieveSizes: ISieveSize[] = useSelector(createWorksheetSelector!('AvailableSieveSizes'));
  const sieveSize: ISieveSize = useSelector(createWorksheetSelector!('SieveSize'));
  const useDirectEntry = useSelector(createWorksheetSelector!('DirectOversizeEntry'));
  const correctionNotRequired = useSelector(
    pipe(createWorksheetSelector!('OversizeCorrectionNotRequired'), valueOrDefault(false))
  );
  const onSieveSizeChanged = (e: any) => {
    const selectedSieve = availableSieveSizes.find((s) => s.Id === e.data.value.value);
    dispatch(actions.setValue({ name: prefixName!('SieveSize'), value: selectedSieve, sync: true }));
  };

  return (
    <>
      <ListFieldSection>
        <WorksheetBalanceFieldSet label="Balance" name="Balance" />
        <Field input={<Checkbox />} label="Direct Entry of Oversize Percent" name="DirectOversizeEntry" />
        {(availableSieveSizes?.length ?? 0) > 1 ? (
          <Field
            input={<Dropdown />}
            label="Sieve Size"
            options={availableSieveSizes?.map((s) => {
              return { value: s.Id, label: s.Caption };
            })}
            value={sieveSize?.Id}
            onChange={onSieveSizeChanged}
            isClearable={false}
          />
        ) : (
          <Field input={<TextOutput />} label="Sieve Size" value={sieveSize?.Caption} />
        )}

        <Field
          input={<WorksheetEquipmentDropdown />}
          label="Sieve ID"
          equipmentType="sieve"
          name="Sieve"
          queryParams={{ SieveSize: `${sieveSize?.Size?.value} ${sieveSize?.Size?.uom}` }}
        />
        <Field input={<TextOutput />} name="MaximumDryDensity" label="Maximum Dry Density" />
        <Field input={<TextOutput />} name="OptimumMoistureContent" label="Optimum Moisture Content (%)" />
        <Field input={<TextOutput />} name="LaboratoryDeterminedOversize" label="Laboratory Determined Oversize (%)" />
        <Field input={<TextOutput />} name="OversizeSpecificGravity" label="Oversize Oven Dry Specific Gravity" />
        {!useDirectEntry && (
          <>
            <Field input={<NumericInput />} name="TareMass" label="Tare Mass" />
            <Field input={<NumericInput />} name="SoilRockAndTareMass" label="Soil, Rock and Tare Mass" />
            <Field input={<TextOutput />} name="SoilAndRockMass" label="Soil and Rock Mass" />
            <Field input={<NumericInput />} name="TareAndRockMass" label="Tare and Rock Mass" />
            <Field input={<TextOutput />} name="RockMass" label="Rock Mass" />
          </>
        )}
      </ListFieldSection>
      <ListFieldSection>
        <Field
          input={useDirectEntry ? <NumericInput /> : <TextOutput />}
          label="Field Oversize (%)"
          name="FieldOversize"
        />
        <Field
          input={<TextOutput />}
          name="Results.CorrectedMaximumDryDensity"
          label="Corrected Maximum Dry Density"
          blankReplacement={correctionNotRequired ? 'N/A' : undefined}
        />
        <Field
          input={<TextOutput />}
          name="Results.CorrectedOptimumMoistureContent"
          label="Corrected Optimum Moisture Content (%)"
          blankReplacement={correctionNotRequired ? 'N/A' : undefined}
        />
      </ListFieldSection>
    </>
  );
};

Worksheet.qestIds = qestIds;
const ArizMeasuredOversizeCorrectionWorksheet = withInjectedProps(Worksheet);

export default ArizMeasuredOversizeCorrectionWorksheet;
export { ArizMeasuredOversizeCorrectionWorksheet };
